import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"

import PlatformShell from "../../components/PlatformAdmin/PlatformShell"
import {
  deletePlatformPendingRegistration,
  listPlatformPendingRegistrations,
  resendPlatformPendingRegistration,
} from "../../client/platform"
import useCustomToast, { getApiErrorMessage } from "../../hooks/useCustomToast"
import { isPlatformLoggedIn, isTenantSubdomainHost } from "../../utils/platformAuth"

export const Route = createFileRoute("/platform/pending-registrations")({
  component: PlatformPendingRegistrationsPage,
  beforeLoad: async () => {
    if (isTenantSubdomainHost()) {
      throw redirect({ to: "/login" })
    }
    if (!isPlatformLoggedIn()) {
      throw redirect({ to: "/platform/login" })
    }
  },
})

function PlatformPendingRegistrationsPage() {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const pendingQuery = useQuery({
    queryKey: ["platformAdmin", "pendingRegistrations"],
    queryFn: listPlatformPendingRegistrations,
  })
  const resendMutation = useMutation({
    mutationFn: resendPlatformPendingRegistration,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "pendingRegistrations"] })
      showToast("Verification resent", "The activation email was sent again.", "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })
  const deleteMutation = useMutation({
    mutationFn: deletePlatformPendingRegistration,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "pendingRegistrations"] })
      showToast("Registration deleted", "Pending registration was removed.", "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })

  return (
    <PlatformShell
      title="Pending registrations"
      description="Rejestracje oczekujące na aktywację i potwierdzenie e-mail."
    >
      {pendingQuery.isLoading ? (
        <Stack spacing={3}>
          <Skeleton h="48px" />
          <Skeleton h="48px" />
          <Skeleton h="48px" />
        </Stack>
      ) : null}

      {pendingQuery.isError ? (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>Failed to load pending registrations.</AlertDescription>
        </Alert>
      ) : null}

      {pendingQuery.data ? (
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Company</Th>
                <Th>Subdomain</Th>
                <Th>Admin email</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingQuery.data.data.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.company_name}</Td>
                  <Td>{item.subdomain}</Td>
                  <Td>{item.admin_email}</Td>
                  <Td>
                    <Badge colorScheme={item.expired ? "red" : "yellow"}>
                      {item.expired ? "expired" : "pending"}
                    </Badge>
                  </Td>
                  <Td>{new Date(item.created_at).toLocaleString()}</Td>
                  <Td>{new Date(item.expires_at).toLocaleString()}</Td>
                  <Td>
                    <Stack direction={{ base: "column", md: "row" }} spacing={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          if (!window.confirm(`Resend verification email to ${item.admin_email}?`)) return
                          resendMutation.mutate(item.id)
                        }}
                        isLoading={resendMutation.isPending}
                      >
                        Resend
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => {
                          if (!window.confirm(`Delete pending registration for ${item.company_name}?`)) return
                          deleteMutation.mutate(item.id)
                        }}
                        isLoading={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Text mt={4} fontSize="sm" color="gray.500">
            Total: {pendingQuery.data.count}
          </Text>
        </Box>
      ) : null}
    </PlatformShell>
  )
}
